/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const ENABLE_PROTOCOL_FEES_DISCRIMINATOR = new Uint8Array([
  9, 115, 79, 19, 158, 209, 221, 38,
]);

export function getEnableProtocolFeesDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ENABLE_PROTOCOL_FEES_DISCRIMINATOR,
  );
}

export type EnableProtocolFeesInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type EnableProtocolFeesInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type EnableProtocolFeesInstructionDataArgs = {};

export function getEnableProtocolFeesInstructionDataEncoder(): FixedSizeEncoder<EnableProtocolFeesInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: ENABLE_PROTOCOL_FEES_DISCRIMINATOR,
    }),
  );
}

export function getEnableProtocolFeesInstructionDataDecoder(): FixedSizeDecoder<EnableProtocolFeesInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getEnableProtocolFeesInstructionDataCodec(): FixedSizeCodec<
  EnableProtocolFeesInstructionDataArgs,
  EnableProtocolFeesInstructionData
> {
  return combineCodec(
    getEnableProtocolFeesInstructionDataEncoder(),
    getEnableProtocolFeesInstructionDataDecoder(),
  );
}

export type EnableProtocolFeesAsyncInput<
  TAccountConfig extends string = string,
  TAccountAuthority extends string = string,
> = {
  config?: Address<TAccountConfig>;
  authority: TransactionSigner<TAccountAuthority>;
};

export async function getEnableProtocolFeesInstructionAsync<
  TAccountConfig extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: EnableProtocolFeesAsyncInput<TAccountConfig, TAccountAuthority>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  EnableProtocolFeesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            112, 114, 111, 116, 111, 99, 111, 108, 95, 99, 111, 110, 102, 105,
            103,
          ]),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.config),
      getAccountMeta(accounts.authority),
    ],
    data: getEnableProtocolFeesInstructionDataEncoder().encode({}),
    programAddress,
  } as EnableProtocolFeesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority
  >);
}

export type EnableProtocolFeesInput<
  TAccountConfig extends string = string,
  TAccountAuthority extends string = string,
> = {
  config: Address<TAccountConfig>;
  authority: TransactionSigner<TAccountAuthority>;
};

export function getEnableProtocolFeesInstruction<
  TAccountConfig extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: EnableProtocolFeesInput<TAccountConfig, TAccountAuthority>,
  config?: { programAddress?: TProgramAddress },
): EnableProtocolFeesInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountAuthority
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.config),
      getAccountMeta(accounts.authority),
    ],
    data: getEnableProtocolFeesInstructionDataEncoder().encode({}),
    programAddress,
  } as EnableProtocolFeesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority
  >);
}

export type ParsedEnableProtocolFeesInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    authority: TAccountMetas[1];
  };
  data: EnableProtocolFeesInstructionData;
};

export function parseEnableProtocolFeesInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedEnableProtocolFeesInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { config: getNextAccount(), authority: getNextAccount() },
    data: getEnableProtocolFeesInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
